import React from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import authService from '../../../services/authService';
import SocialButtons from './SocialButtons';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as userSlice from '../../../data/user.slice';

const PREFIX = 'LoginPageForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  formBody: `${PREFIX}-formBody`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  linkText: `${PREFIX}-linkText`,
  submit: `${PREFIX}-submit`,
  divider: `${PREFIX}-divider`,
  icon: `${PREFIX}-icon`,
  dividerWrapper: `${PREFIX}-dividerWrapper`,
  dividerLine: `${PREFIX}-dividerLine`,
  dividerText: `${PREFIX}-dividerText`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  [`& .${classes.form}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  [`& .${classes.formBody}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    width: '100%',
  },
  [`& .${classes.linkText}`]: {
    textTransform: 'none',
    justifyContent: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
    fontWeight: 400,
    color: theme.palette.buttonColor.main,
    textDecoration: 'none',
    fontSize: theme.typography.body2.fontSize,
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.buttonHoverColor.main,
      textDecoration: 'underline',
      backgroundColor: theme.palette.background.default,
    },
  },
  [`& .${classes.submit}`]: {
    width: '100%',
  },
  [`& .${classes.dividerWrapper}`]: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
  },

  [`& .${classes.dividerLine}`]: {
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.divider,
  },
  [`& .${classes.dividerText}`]: {
    fontWeight: 600,
  },
  [`& .${classes.icon}`]: {
    marginRight: theme.spacing(2),
    width: 20,
    height: 20,
  },
}));

export default function LoginPageForm({
  variant,
}: {
  variant: 'client' | 'member';
}) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const { register, handleSubmit } = useForm();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const isClient = variant === 'client';

  const [loginError, setLoginError] = React.useState<string>('');

  const handleEmailSubmit = (info: any) => {
    setLoginError('');
    authService
      .memberLogin(info.email, info.password)
      .then(() => {
        dispatch(userSlice.setLoggedInUserInitialized(false));
        dispatch(userSlice.fetchLoggedInUserInfoAsync());
        navigate('/home');
      })
      .catch((err) => setLoginError(String(err) || ''));
  };

  return (
    <Root className={classes.container}>
      <Box className={classes.form}>
        <Typography variant="h3" component="h1" className={classes.title}>
          {t('member.login')}
        </Typography>

        <form
          onSubmit={handleSubmit(handleEmailSubmit)}
          className={classes.formBody}
          noValidate>
          <TextField
            fullWidth
            label={t('passenger.email')}
            type="email"
            variant="outlined"
            required
            {...register('email', { required: true })}
          />
          <TextField
            fullWidth
            label={t('users.password')}
            variant="outlined"
            required
            {...register('password', { required: true })}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={t('login.togglePassword')}>
                    {showPassword ? (
                      <VisibilityOffOutlined />
                    ) : (
                      <VisibilityOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {loginError && (
            <Typography variant="body2" color="error">
              {loginError}
            </Typography>
          )}
          <Button
            variant="text"
            size="small"
            className={classes.linkText}
            onClick={() =>
              navigate(`/memberResetPassword?isClientPortal=${isClient}`)
            }>
            {t('member.forgotPassword')}
          </Button>

          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.submit}
            type="submit">
            {t('member.continue')}
          </Button>
        </form>

        {!isClient && (
          <>
            <Typography variant="body2">
              {`${t('member.noAccount')} `}
              <Link to="/memberCreateAccount" className={classes.linkText}>
                {t('member.createAccount')}
              </Link>
            </Typography>

            <Box className={classes.dividerWrapper}>
              <Box className={classes.dividerLine} />
              <Typography
                variant="h5"
                component="p"
                className={classes.dividerText}>
                {t('member.or')}
              </Typography>
              <Box className={classes.dividerLine} />
            </Box>

            <SocialButtons />
          </>
        )}
      </Box>
    </Root>
  );
}
